import type { ReactNode } from 'react';
import type { TrackedRequest } from '../../types';
import { cx } from '../ui/primitives';

/** Presentation shared by the requests list, the tab list and the push form. */

export function formatDateTime(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export type Tone = 'done' | 'partial' | 'none';

const TONE_CLASS: Record<Tone, string> = {
  done: 'bg-success/10 text-success ring-1 ring-inset ring-success/20',
  partial: 'bg-warning/10 text-warning ring-1 ring-inset ring-warning/25',
  none: 'bg-surface-muted text-ink-muted ring-1 ring-inset ring-line',
};

export function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span
      className={cx(
        'inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-micro font-medium',
        TONE_CLASS[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * Push progress as a phrase.
 *
 * Counted in cohorts rather than pushes: a cohort pushed twice is still one
 * cohort covered, and "7 of 6 pushed" would be nonsense.
 */
export function pushProgress(pushed: number, cohorts: number): { text: string; tone: Tone } {
  if (!cohorts) return { text: 'No cohorts', tone: 'none' };
  if (pushed === 0) return { text: `0 / ${cohorts} pushed`, tone: 'none' };
  if (pushed < cohorts) return { text: `${pushed} / ${cohorts} pushed`, tone: 'partial' };
  return { text: `All ${cohorts} pushed`, tone: 'done' };
}

/** `Agency - Sub Agency`, or the workflow name for a direct brand. */
export function agencyLabel(entry: {
  businessType: TrackedRequest['businessType'];
  agencyName: string;
  subAgencyName: string;
}): string {
  if (entry.businessType !== 'AGENCY') return 'Direct Brand';
  return [entry.agencyName, entry.subAgencyName].filter(Boolean).join(' - ') || '—';
}
